import { FC } from "react";
import { Stack, TableCell, TableRow } from "@mui/material";
import { commitmentsResourceTypeNames } from "../../../../../common/utils/constants/labels";
import { ResourceTypeCoverage } from "@/services/cloudchipr.api";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { CommitmentsProgressBar } from "@/components/pages/commtiments/common/components/CommitmentsProgressBar";
import { money } from "@/utils/numeral/money";
import { CommitmentsOverviewSectionLegend } from "@/components/pages/commtiments/overview/content/components/common/CommitmentsOverviewSectionLegend";
import { useHover } from "@/utils/hooks/useHover.hook";
import { CommitmentsOverviewCoverageTableViewCoverageLink } from "@/components/pages/commtiments/overview/content/components/common/coverage-table/CommitmentsOverviewCoverageTableViewCoverageLink";

interface CommitmentsOverviewCoverageTableRowProps {
  resourceCoverageItem: ResourceTypeCoverage;
}

export const CommitmentsOverviewCoverageTableRow: FC<
  CommitmentsOverviewCoverageTableRowProps
> = ({ resourceCoverageItem }) => {
  const { ref, hovered } = useHover();

  const resourceType = resourceCoverageItem.resource_type;
  const coverage = resourceCoverageItem.coverage;
  const covered = resourceCoverageItem.coverage;
  const notCovered = +(100 - covered).toFixed(2);
  const uncoveredUsage = resourceCoverageItem.uncovered_usage;
  const coveredUsage = getCoveredUsage(uncoveredUsage, notCovered);

  return (
    <TableRow ref={ref} sx={{ "&:hover": { backgroundColor: "action.hover" } }}>
      <TableCell sx={tableCellSx}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Stack flex={1} direction="row" alignItems="center" spacing={1}>
              <TypographyWithTooltip
                title={
                  commitmentsResourceTypeNames?.[resourceType] ?? resourceType
                }
                variant="body2"
                fontWeight="bold"
              />

              <CommitmentsOverviewCoverageTableViewCoverageLink
                resourceType={resourceType}
                hovered={hovered}
              />
            </Stack>

            <Stack spacing={0.5} direction="row" alignItems="center">
              <CommitmentsOverviewSectionLegend
                label={`${covered}%`}
                color="info.light"
              />
              <TypographyWithTooltip
                variant="body2"
                fontWeight="medium"
                textAlign="end"
                color="text.secondary"
                title={
                  uncoveredUsage === null ? "N/A" : `(${money(coveredUsage)})`
                }
              />
            </Stack>

            <Stack spacing={0.5} direction="row" alignItems="center">
              <CommitmentsOverviewSectionLegend
                label={`${notCovered}%`}
                color="grey.400"
              />
              <TypographyWithTooltip
                variant="body2"
                fontWeight="medium"
                textAlign="end"
                color="text.secondary"
                title={
                  uncoveredUsage === null ? "N/A" : `(${money(uncoveredUsage)})`
                }
              />
            </Stack>
          </Stack>
          <CommitmentsProgressBar
            value={coverage}
            overrideColor="info.light"
            width="100%"
            hideValueLabel
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const tableCellSx = {
  py: 1.5,
  borderBottom: "none",
};

const getCoveredUsage = (uncoveredUsage: number, notCovered: number) => {
  const total = (uncoveredUsage * 100) / notCovered;
  return +(total - uncoveredUsage).toFixed(2);
};
